import { useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/base/dialog";
import { Button } from "@/components/ui/base/button";
import type { MerchantsRepository } from "@/application/merchants/merchants_repository";

interface Props {
  businessId: string;
  businessName: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful purge so the caller can navigate away. */
  onPurged: () => void;
  repo: MerchantsRepository;
}

/**
 * Irreversible "Delete permanently" confirmation for a merchant. Requires the
 * developer to type the exact business name before the destructive button
 * enables — the merchant no longer exists to undo against, so a stray click
 * must not be enough. Backed by the CPOS-Developers-gated `purgeMerchant`
 * mutation, which itself refuses to run in production.
 */
export function PurgeMerchantDialog({
  businessId,
  businessName,
  open,
  onOpenChange,
  onPurged,
  repo,
}: Props) {
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const expected = businessName ?? businessId;
  const canDelete = confirmText.trim() === expected && !submitting;

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen && submitting) return;
    if (!nextOpen) setConfirmText("");
    onOpenChange(nextOpen);
  }

  async function handleDelete() {
    if (!canDelete) return;
    setSubmitting(true);
    try {
      const result = await repo.purgeMerchant(businessId);
      const totalRecords = Object.values(result.deleted).reduce(
        (sum, n) => sum + n,
        0,
      );
      const allErrors = [...result.cognitoErrors];
      if (result.error) allErrors.push(result.error);

      if (allErrors.length === 0) {
        toast.success(
          `Merchant deleted — ${totalRecords} records, ${result.cognitoDeleted} logins removed`,
        );
      } else {
        toast(
          `Merchant deleted with warnings (${allErrors.length}) — check logs`,
          { icon: "⚠️" },
        );
      }
      handleOpenChange(false);
      onPurged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            Delete merchant permanently
          </DialogTitle>
          <DialogDescription>
            This permanently deletes{" "}
            <strong className="text-foreground">{expected}</strong> and cannot
            be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <p className="font-medium">This will permanently remove:</p>
            <ul className="mt-1 list-disc pl-5">
              <li>The business profile and lifecycle</li>
              <li>
                All transactions, receipts, payouts, invoices, staff & settings
              </li>
              <li>Every Cognito login attached to the business</li>
            </ul>
          </div>

          <div>
            <label
              htmlFor="purge-confirm"
              className="text-foreground mb-1 block text-xs font-medium"
            >
              Type <span className="text-foreground font-mono">{expected}</span>{" "}
              to confirm
            </label>
            <input
              id="purge-confirm"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={expected}
              disabled={submitting}
              autoComplete="off"
              className="border-border w-full rounded-md border px-3 py-2 text-sm outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 disabled:opacity-50"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={!canDelete}
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
